import { createApi } from "@reduxjs/toolkit/query/react";
import { getCurrentUserId } from "features/authSlice";
import { baseQueryWithAuth } from "services/common/rtkQueryConfig";
import { DOCUMENT_UPLOAD_TIMEOUT_MS } from "utilities/Constant";

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["dashboard", "document", "document-dealApplication"],
  endpoints: (build) => ({
    getLoanStatus: build.query<LoanProgressData, { nhDealApplicationId: string }>({
      query: ({ nhDealApplicationId }) => `/dashboard/v2/nh-deal-applications/${nhDealApplicationId}/loan-status`,
    }),
    getDashboardItems: build.query<DashboardItemsResponseDto, { dealApplicationId: string; userId: string }>({
      query: ({ dealApplicationId, userId }) => `/dashboard/nhDealApplication-Id/${dealApplicationId}/userId/${userId}/outstanding-items`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
      keepUnusedDataFor: 0,
    }),
    getNeedsListV1: build.query<PendingItem[], { nhDealApplicationId: string; userId: string }>({
      query: ({ nhDealApplicationId, userId }) => `/dashboard/nhDealApplicationId/${nhDealApplicationId}/userId/${userId}/conditions`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
    }),
    getNeeds: build.query<NeedItem[], { nhDealApplicationId: string; userId: string }>({
      query: ({ nhDealApplicationId, userId }) => `/needs/v2/nh-deal-application/${nhDealApplicationId}/user/${userId}`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
    }),
    markESignFSEventSent: build.mutation<
      void,
      { nhDealApplicationId: string; prospectId: string; packageId: string; titlePrefix: string; eventType: ESignFSEventType }
    >({
      query: ({ nhDealApplicationId, prospectId, packageId, titlePrefix, eventType }) => ({
        url: `/needs/v2/nh-deal-application/${nhDealApplicationId}/prospect/${prospectId}/package/${packageId}/title-prefix/${encodeURIComponent(
          titlePrefix,
        )}/fs-event/${eventType}`,
        method: "POST",
      }),
    }),
    setStatusFlagOfUser: build.mutation<void, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/dashboard/nhDealApplication-Id/${id}/userId/${userId}`,
        method: "PATCH",
      }),
      invalidatesTags: ["dashboard"],
      async onQueryStarted({ id, userId }, { dispatch, queryFulfilled }) {
        dispatch(
          dashboardApi.util.updateQueryData("getDashboardItems", { dealApplicationId: id, userId }, (draft) => {
            draft.isNew = false;
          }),
        );
        await queryFulfilled.catch(() => {});
      },
    }),
    setPalStatusFlagOfUser: build.mutation<void, string>({
      query: (id) => ({
        url: `/dashboard/deal-application/${id}/pal-status-flag`,
        method: "PATCH",
      }),
      invalidatesTags: ["dashboard"],
    }),
    updateDocumentIds: build.mutation<boolean, DocumentSyncRequest>({
      query: ({ nhDealApplicationId, borrowerId, needId, documentUpdateDto }: DocumentSyncRequest) => ({
        url: `/needs/v2/nh-deal-application/${nhDealApplicationId}/user/${borrowerId}/need/${needId}/document-ids`,
        method: "POST",
        body: documentUpdateDto,
      }),
      invalidatesTags: ["document"],
    }),
    updateDocumentIdsWithoutNeeds: build.mutation<boolean, DocumentSyncRequest>({
      query: ({ nhDealApplicationId, borrowerId, documentUpdateDto }: DocumentSyncRequest) => ({
        url: `/needs/v2/nh-deal-application/${nhDealApplicationId}/user/${borrowerId}/document-ids`,
        method: "POST",
        body: documentUpdateDto,
      }),
      invalidatesTags: ["document"],
    }),
    retrieveDocuments: build.query<RetrieveDocumentsResponseDto[], string>({
      query: (applicationId) => `/dashboard/RetrieveDocumentsAsync/${applicationId}`,
      keepUnusedDataFor: 0.2,
    }),
    retrieveNeeds: build.query<RetrieveNeedDto[], { nhDealApplicationId: string; userId?: string }>({
      query: ({ nhDealApplicationId, userId }) => {
        if (!nhDealApplicationId) {
          throw new Error("nhDealApplicationId is required");
        }
        const baseUrl = `/needs/v2/nh-deal-application/${nhDealApplicationId}/needs-with-details`;
        return userId ? `${baseUrl}?userId=${userId}` : baseUrl;
      },
      keepUnusedDataFor: 0.2,
    }),

    retrieveDocumentsForDealApplication: build.query<RetrieveDocumentsResponseDto[], string>({
      query: (applicationId) => `/dashboard/deal-application/${applicationId}`,
      keepUnusedDataFor: 0.2,
    }),
    retrieveDocumentContent: build.query<RetrieveDocumentContent, string>({
      query: (fileId) => `/dashboard/RetrieveDocumentContentAsync/${fileId}`,
    }),
    retrievePalContent: build.query<RetrievePalResponseDto[], ReteievePalRequestDto>({
      query: ({ nhDealApplicationId, loanId }: ReteievePalRequestDto) =>
        `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/loan/${loanId}/approval-letters`,
    }),
    maticResponse: build.mutation<string, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/dashboard/nhDealApplication-id/${id}/userId/${userId}/redirect-matic`,
        method: "POST",
        responseHandler: (response) => response.text(),
      }),
    }),
    getDashboardBanner: build.query<DashboardAdDto, string>({
      query: (id) => `/dashboard/nhDealApplication-id/${id}/ad`,
    }),
    checkCreditScore: build.query<boolean, { nhDealApplicationId: string; prospectId: string }>({
      query: ({ nhDealApplicationId, prospectId }) => `/dashboard/deal-application/${nhDealApplicationId}/prospect/${prospectId}/credit-score`,
    }),
    createDashboardSession: build.mutation<void, { nhDealApplicationId: string; userId: string; dashboardSession: DashboardSession }>({
      query: ({ nhDealApplicationId, userId, dashboardSession }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/user/${userId}/session-start`,
        method: "POST",
        body: dashboardSession,
      }),
    }),
    updatePartnerSession: build.mutation<void, { nhDealApplicationId: string; userId: string; partnerAdId: string }>({
      query: ({ nhDealApplicationId, userId, partnerAdId }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/user/${userId}/partner-session/${partnerAdId}`,
        method: "POST",
      }),
    }),
    UpdateTaskMessage: build.mutation<void, { nhDealApplicationId: string; conditionId: string; userId: string }>({
      query: ({ nhDealApplicationId, conditionId, userId }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/condition/${conditionId}/user/${userId}/update-task`,
        method: "POST",
      }),
    }),
    // upload document using NhDealApplicationId
    uploadDocuments: build.mutation<string, { nhDealApplicationId: string; formData: FormData }>({
      query: ({ nhDealApplicationId, formData }) => ({
        url: `/needs/v2/nh-dealapplication/${nhDealApplicationId}/document`,
        method: "POST",
        body: formData,
        timeout: DOCUMENT_UPLOAD_TIMEOUT_MS,
        headers: { Connection: "close" },
      }),
      // invalidatesTags: ["document-dealApplication"],
    }),
    createNeeds: build.mutation<boolean, { dealApplicationId: string; userId: string; isInterimDashboard: boolean }>({
      query: ({ dealApplicationId, userId, isInterimDashboard }) => ({
        url: `/needs/deal-application/${dealApplicationId}/user/${userId}/interim/${String(isInterimDashboard)}`,
        method: "POST",
      }),
    }),
    deleteDocument: build.mutation<string, { nhDealApplicationId: string; needId: string; documentId: string }>({
      query: ({ nhDealApplicationId, needId, documentId }) => ({
        url: `/needs/v2/${needId}/nh-deal-application/${nhDealApplicationId}/document/${documentId}/`,
        method: "DELETE",
      }),
    }),
    viewedDocumentsLog: build.mutation<void, { userId: string; documentIds: string[] }>({
      query: ({ userId, documentIds }) => ({
        url: `CyberSecurityAudit/userId/${userId}/viewed-documents`,
        method: "POST",
        body: documentIds,
      }),
    }),
    downloadedDocumentsLog: build.mutation<void, { userId: string; documentIds: string[] }>({
      query: ({ userId, documentIds }) => ({
        url: `CyberSecurityAudit/userId/${userId}/downloaded-documents`,
        method: "POST",
        body: documentIds,
      }),
    }),
    setEdisclosureStatusToUnderReview: build.mutation<void, { nhDealApplicationId: string; prospectId: string; packageId: string }>({
      query: (body) => ({
        url: `/needs/v2/edisclosure/under-review`,
        method: "POST",
        body,
      }),
    }),
    getBorrowerDetails: build.query<BorrowerDetails, string>({
      query: (userId) => `/user/getUserProfile/${userId}`,
    }),

    getLoanSummaryDetail: build.query<LoanSummaryDto, { nhDealApplicationId: string; loanId: string; persistState: boolean }>({
      query: ({ nhDealApplicationId, loanId, persistState }) =>
        `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/loan/${loanId}/loan-summary${persistState ? "/persist" : ""}`,
    }),

    getEncompassLoanData: build.query<EncompassLoanDataDto, { nhDealApplicationId: string }>({
      query: ({ nhDealApplicationId }) => `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/encompass-loan-data`,
    }),

    getUserStateLogic: build.query<string, { loanId: string; nhDealApplicationId: string; userId: string }>({
      query: ({ loanId, nhDealApplicationId, userId }) => ({
        url: `/dashboard/v2/loans/${loanId}/nh-deal-applications/${nhDealApplicationId}/users/${userId}`,
        responseHandler: (response) => response.text(),
      }),
    }),

    getExtendedLoanDetails: build.query<ExtendedLoanDetails, { nhDealApplicationId: string; userId: string }>({
      query: ({ nhDealApplicationId, userId }) => `/dashboard/nhDealApplicationId/${nhDealApplicationId}/userId/${userId}/extended-loan-details`,
      // Keep cache effectively for the whole app runtime (seconds) so this call happens once per session.
      keepUnusedDataFor: 2147483647,
    }),

    getBorrowerIdentifiers: build.query<DealBorrowerIdentifiers, { loanId: string; applicationId: string }>({
      query: ({ loanId, applicationId }) =>
        `/deals/loans/${loanId}/applicaions/${applicationId}/borrower-identifiers`,
      keepUnusedDataFor: 0,
    }),

    getBorrowerPersonalInformation: build.query<BorrowerPersonalInformation, { borrowerId: string }>({
      query: ({ borrowerId }) =>
        `/deal/borrowers/${borrowerId}/personal-information`,
      keepUnusedDataFor: 0,
    }),

    updateUserPreferredLanguage: build.mutation<void, { userId: string; preferredLanguage: string }>({
      query: ({ userId, preferredLanguage }) => ({
        url: `/user/${userId}/preferred-language`,
        method: "PATCH",
        body: { preferredLanguage },
      }),
    }),
  }),
});

export const {
  useGetLoanStatusQuery,
  useGetDashboardItemsQuery,
  useGetNeedsListV1Query,
  useSetStatusFlagOfUserMutation,
  useUpdateDocumentIdsMutation,
  useUpdateDocumentIdsWithoutNeedsMutation,
  useRetrieveDocumentsQuery,
  useRetrieveNeedsQuery,
  useRetrieveDocumentsForDealApplicationQuery,
  useRetrieveDocumentContentQuery,
  useRetrievePalContentQuery,
  useMaticResponseMutation,
  useGetDashboardBannerQuery,
  useSetPalStatusFlagOfUserMutation,
  useCheckCreditScoreQuery,
  useCreateDashboardSessionMutation,
  useUpdatePartnerSessionMutation,
  useUpdateTaskMessageMutation,
  useUploadDocumentsMutation,
  useDeleteDocumentMutation,
  useCreateNeedsMutation,
  useViewedDocumentsLogMutation,
  useDownloadedDocumentsLogMutation,
  useSetEdisclosureStatusToUnderReviewMutation,
  useGetBorrowerDetailsQuery,
  useGetLoanSummaryDetailQuery,
  useGetEncompassLoanDataQuery,
  useGetNeedsQuery,
  useMarkESignFSEventSentMutation,
  useGetUserStateLogicQuery,
  useGetExtendedLoanDetailsQuery,
  useGetBorrowerIdentifiersQuery,
  useGetBorrowerPersonalInformationQuery,
  useUpdateUserPreferredLanguageMutation,
} = dashboardApi;
export interface DashboardItemsResponseDto {
  loanStatus: LoanStatus;
  propertyInfo: PropertyInfo;
  closingDate?: Date;
  isNew: boolean;
  isPalViewed: boolean;
  isUqualSelfReferralDone?: boolean;
  isAwaitingNeedsCreation?: boolean;
  isApplicationFormRequired?: boolean;
  isCoborrowerNew: boolean;
  showUqualBanner?: boolean;
  showUqualNotification?: boolean;
}

export type LoanStatusType = "Started" | "Processing" | "Underwriting" | "Approval" | "FinalReview" | "Close" | "Funded" | "Unknown";

export type LoanSubStatusType = "UnderReview" | "ConditionallyApproved";

export type LoanStatusTypeVersion2 = "Started" | "Processing" | "PreApproved" | "InitialApproval" | "ClearToClose" | "Closed" | "Unknown";

export interface LoanProgressData {
  status: LoanStatusTypeVersion2;
  isPreApproved: boolean;
}

export interface PropertyLocation {
  addressLine1?: string;
  addressLine2?: string;
  addressLineText?: string;
  addressUnitType?: string;
  addressUnitIdentifier?: string;
  cityName?: string;
  stateCode?: string;
  postalCode?: string;
}

export interface LoanSummaryDto {
  propertyLocation?: PropertyLocation;
  loanPurpose?: string;
  loanAmount?: number;
  loanNumber?: string;
  purchasePrice?: number;
  totalDownPaymentAmount?: number;
  losId?: string;
  updatedFields?: string[];
}

export interface PropertyAddress {
  streetAddress?: string;
  addressUnitDesignatorType?: string | null;
  addressUnitIdentifier?: string | null;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface EncompassLoanDataDto {
  loanSource?: string;
  loanNumber?: string;
  loanPurpose?: string;
  baseLoanAmount?: number;
  borrowerRequestedLoanAmount?: number;
  purchasePriceAmount?: number;
  downPaymentPercent?: number;
  property?: PropertyAddress;
  loanAmortizationTermMonths?: number;
  mortgageType?: string;
  occupancyType?: string;
  initialInterestRate?: number;
  actualMfiHundredPercent?: number;
  rateLock?: {
    buySideExpirationDate?: string;
    lockValidationStatus?: string;
  };
  loanAmortizationType?: string;
  principalAndInterestMonthlyPaymentAmount?: number;
  proposedRealEstateTaxesAmount?: string | null;
  proposedHazardInsuranceAmount?: string | null;
  proposedDuesAmount?: string | null;
  proposedOtherAmount?: number;
  supplementalPropertyInsuranceAmount?: number | null;
  proposedOtherMortgagesAmount?: number;
  proposedHousingExpenseTotal?: number;
  regulationZ?: {
    disclosedAprPercent?: number;
  };
  closingDocument?: {
    actualRescindedDate?: string;
    closingState?: string;
    loanIsLocked?: boolean;
  };
  loanProductData?: {
    loanScheduledClosingDate?: string;
  };
  loanSubmission?: {
    lockExpiresDate?: string;
  };
}

export interface LoanDetails extends PropertyAddress {
  purpose?: string;
  number?: string;
  amount?: number;
}

export interface ExtendedLoanDetails extends LoanDetails {
  purchasePrice?: number;
  downPayment?: number;
  downPaymentPercent?: number;
  loanAmortizationTermMonths?: number;
  mortgageType?: string;
  occupancyType?: string;
  initialInterestRate?: number;
  aprPercent?: number;
  loanIsLocked?: boolean;
  loanAmortizationType?: string;
  principalAndInterestMonthlyPaymentAmount?: number;
  proposedRealEstateTaxesAmount?: string;
  proposedHazardInsuranceAmount?: string;
  proposedDuesAmount?: string | null;
  proposedOtherAmount?: number;
  supplementalPropertyInsuranceAmount?: number;
  mortgageInsuranceAmount?: string | null;
  proposedOtherMortgagesAmount?: number;
  proposedHousingExpenseTotal?: number;
  estimatedClosingDate?: string;
  rateLockExpirationDate?: string;
  updatedFields?: string[];
}

interface DocumentUpdateDto {
  isInitialDocumentUploaded: boolean;
}

interface DocumentSyncRequest {
  nhDealApplicationId: string;
  borrowerId: string;
  needId: string;
  documentUpdateDto: DocumentUpdateDto;
}

interface DashboardAdDto {
  image: string;
  logo: string;
  imageText: string;
  description1: string;
  description2: string;
}

export interface LoanStatus {
  status: LoanStatusType;
  subStatus?: LoanSubStatusType;
}

export interface PropertyInfo {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  loanNumber: string;
  loanAmount: number;
  loanType: string;
}

export interface PendingItem {
  title: string;
  status: "New" | "Submitted" | "Error" | "Pending Approval" | "Returned" | "needsAttention" | "Completed" | "underReview";
  id: string;
  note?: string;
  needStatus?: NeedStatus;
  documents?: Document[];
  needType?: NeedType;
  isEnabled?: boolean;
  lat?: string;
  packageId?: string;
  nboContactId?: string;
  prospectId?: string;
  assignedTo?: string;
  isDefault?: boolean;
}

export interface NeedItem {
  id: string;
  title: string;
  assignedTo?: string;
  needStatus?: NeedStatus;
  documents?: Document[];
  needType?: NeedType;
  note?: string;
  lat?: string;
  packageId?: string;
  nboContactId?: string;
  prospectId?: string;
  isDefault?: boolean;
  isEnabled?: boolean;
  isNewComment?: boolean;
  isEsignCreatedFSEventSent?: boolean;
  isEsignStartedFSEventSent?: boolean;
  isEsignCompletedFSEventSent?: boolean;
  taskLoadErrorType?: string;
  needRequestTypeId?: string;
}

export interface RetrieveDocumentContent {
  fileContents: string;
  contentType: string;
}
export interface DashboardSession {
  dashboardSessionId: string;
  dashboardSessionStartTimestamp: string | null;
  dashboardSessionEndTimestamp: string | null;
}
export interface RetrieveDocumentsResponseDto {
  fileId: string;
  name: string;
  documentPath: string;
  dateCreated: string;
  category: string;
  userName?: string;
  invalidReason?: string;
  detailedExplanation?: string;
  userNotified?: boolean;
  status?: DocumentStatus;
  isNoPreview?: boolean;
}

export interface DocumentsRequestDto {
  applicationId?: string;
}

export interface DocumentsRequestForDealApplicationDto {
  dealApplicationId?: string;
}

export interface RetrievePalResponseDto {
  palInstanceId: string;
  createdDate?: string;
  documentContent?: RetrievePalContent;
  documents: Document[];
}
interface RetrievePalContent {
  fileContents: string;
  contentType: string;
  name: string;
}

export interface ReteievePalRequestDto {
  nhDealApplicationId: string;
  loanId: string;
}

export interface GetConditionsRequestDto {
  nhDealApplicationId: string;
  isNeedsListV2Enabled: boolean;
}

export interface RetrieveNeedDto {
  id: string;
  borrowerId: string; // Null for "both borrowers"
  title: string;
  isDefault: boolean;
  isEncompassUpload: boolean;
  documents: Document[];
  isNoPreview: boolean;
  isCanBeUsedForDocumentUpload: boolean;
}

export interface Document {
  id: string;
  uploadedDateTime: string;
  docName: string;
  uploader: Uploader;
  invalidReason?: string;
  detailedExplanation?: string;
  userNotified?: boolean;
  status?: DocumentStatus;
  isNoPreview?: boolean;
}

interface Uploader {
  firstName: string;
  lastName: string;
  emailAddress: string;
}

export enum NeedStatus {
  Unknown = "Unknown",
  ReviewNeeded = "ReviewNeeded",
  PendingDocuments = "PendingDocuments",
  Satisfied = "Satisfied",
  Completed = "Completed",
  New = "New",
}

export enum DocumentStatus {
  PendingReview = "PendingReview",
  Accepted = "Accepted",
  Rejected = "Rejected",
}

export enum NeedType {
  Needs = "needs",
  ESign = "eSign",
  EnhancedConditions = "enhancedConditions",
  Voa = "voa",
}

export enum ESignFSEventType {
  Created = "Created",
  Started = "Started",
  Completed = "Completed",
}

export interface BorrowerDetails {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  preferredLanguage?: string;
}

export const ESignStatus = {
  NEW: "new",
  COMPLETED: "completed",
  UNDER_REVIEW: "underreview",
};

export interface DealBorrowerIdentifiers {
  borrowerId?: string;
  coBorrowerId?: string;
}

export interface BorrowerPersonalInformation {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  email?: string;
  dateOfBirth?: string;
  citizenship?: string;
  borrowerCount?: number;
  dealBorrowerId?: string;
  phones?: { phoneNumber?: string; phoneType?: string }[];
  mailingAddress?: {
    addressLineText?: string;
    cityName?: string;
    stateCode?: string;
    postalCode?: string;
    countryCode?: string;
  };
  language?: {
    preferredLanguage?: string;
  };
  preferredLanguage?: {
    languageType?: string;
  }; 
}
